import { Alert, Button, Input, Spinner, Textarea, Typography } from '@material-tailwind/react'
import { colors } from '@material-tailwind/react/types/generic'
import isEmpty from 'lodash/isEmpty'
import React, { useCallback, useEffect, useState } from 'react'
import { PiCircleDashed } from 'react-icons/pi'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import { logger } from '../utils/helpers/logger'
import AgamiPreGeneratedSignedUrl from '../components/AgamiPreGeneratedSignedUrl'
import SpotPositionPreview from '../components/SpotPositionPreview'
import { useSessionContext } from '../contexts/SessionContext'
import { useFeatureAuth } from '../hooks/authorization'
import { useFormData } from '../hooks/form'
import { useGeneralToast } from '../hooks/toast'
import { useUserExperiences } from '../hooks/useUserExperiences'
import { AGAMI_GLOBAL_EXPERIENCE_ID } from '../utils/constants/db.constants'
import { FEATURES } from '../utils/constants/features.constants'
import { hasPendingSubmission, removeManualCodeLibrarySuffix } from '../utils/helpers/agami.helpers'
import ProposalBanner from '../components/ProposalBanner'
import { formatDateTimeString } from '../utils/helpers/dates.helpers'
import { cleanFileName, upsertAgamiSpot } from '../utils/helpers/upload.helpers'
import AgamiExperienceSelector from './AgamiExperienceSelector'
import AgamiMediaFileSelector from './AgamiMediaFileSelector'
import AgamiTypeSelector from './AgamiTypeSelector'
import AgamiVisibilitySelector from './AgamiVisibilitySelector'
import SpotManualCodeSelector from './SpotManualCodeSelector'
import LandingPageSelector from './LandingPageSelector'
import SpotPositionSelector from './SpotPositionSelector'
import { useDarkMode } from '../contexts/DarkModeContext'
import { AgamiSpot } from '../types/supabase'
import { DEFAULT_SPOT_DEADBAND, DEFAULT_SPOT_RADIUS } from '../utils/constants/geoxp.constants'

export interface AgamiUploadFormData {
  experiences: string[]
  spotName: string
  description: string
  latitude?: number
  longitude?: number
  radius?: number
  deadband?: number
  type: number
  manual_code?: string
  content_media_type: number
  landing_page_code_id?: string | null
  base_language?: string | null
}

interface UploadAgamiInterfaceProps {
  availableSpotTypes: Record<number, string>
  agami?: AgamiSpot
  refetchAgami?: () => void
}

const UploadAgamiInterface: React.FC<UploadAgamiInterfaceProps> = ({
  availableSpotTypes,
  agami,
  refetchAgami,
}) => {
  const { t } = useTranslation()
  const { user } = useSessionContext()
  const { error: toastError, success: toastSuccess } = useGeneralToast()
  const [mediaFile, setMediaFile] = useState<File | null>(null)
  const navigate = useNavigate()

  const { darkMode } = useDarkMode()

  const manualSpotsAuth = useFeatureAuth(FEATURES.MANUAL_SPOTS)
  const { experienceList: userExperiences } = useUserExperiences({
    excludeDefaultExperience: true,
  })

  const existingSpotEdit = React.useMemo(() => !!agami, [agami])

  const [searchParams] = useSearchParams()
  const addToExperience = searchParams.get('addToExperience')

  const initialFormData = React.useMemo<AgamiUploadFormData>(() => {
    // if editing an existing agami
    if (existingSpotEdit && agami) {
      return {
        experiences: agami.experience_content_spots?.map((exp) => exp.experience_id) ?? [],
        spotName: agami.name,
        description: agami.description,
        latitude: agami.latitude,
        longitude: agami.longitude,
        radius: agami.radius,
        deadband: agami.deadband,
        type: agami.type,
        manual_code: removeManualCodeLibrarySuffix(agami.experience_content_spots?.[0]?.manual_code),
        content_media_type: agami.content_media_type ?? 1,
        landing_page_code_id: agami.landing_page_code_id ?? null,
      }
    }

    // if creating a new agami
    const experiences = addToExperience
      ? [addToExperience, user!.defaultExperienceId]
      : [user!.defaultExperienceId]

    return {
      experiences,
      spotName: '',
      description: '',
      type: 1,
      manual_code: '',
      content_media_type: 1,
      landing_page_code_id: null,
    }
  }, [existingSpotEdit, agami, addToExperience])

  const { formData, setFormData, handleDataChange, checkInputIsValid } =
    useFormData<AgamiUploadFormData>(initialFormData)

  // if user has a private library, give the chance to chose between public and private
  const showMakePublicOption = user?.defaultExperienceId !== AGAMI_GLOBAL_EXPERIENCE_ID

  // already in global exp (so already public)?
  const isInGlobalExp = agami?.experience_content_spots
    ?.map((exp) => exp.experience_id)
    ?.includes(AGAMI_GLOBAL_EXPERIENCE_ID)

  const [isPublic, setIsPublic] = useState(!showMakePublicOption || isInGlobalExp)
  const [uploading, setUploading] = useState(false)

  useEffect(() => {
    if (isPublic && !formData.experiences.includes(AGAMI_GLOBAL_EXPERIENCE_ID)) {
      handleDataChange('experiences', (currExps: string[]) => [...currExps, AGAMI_GLOBAL_EXPERIENCE_ID])
    }

    if (!isPublic && formData.experiences.includes(AGAMI_GLOBAL_EXPERIENCE_ID)) {
      handleDataChange('experiences', (currExps: string[]) =>
        currExps.filter((e) => e !== AGAMI_GLOBAL_EXPERIENCE_ID)
      )
    }
  }, [isPublic, formData.experiences])

  const onSuccess = useCallback(() => {
    setUploading(false)

    // new upload
    if (!agami?.id) {
      toastSuccess(t('uploadAgamiInterface.uploadSuccess'))
      const targetUrl = addToExperience ? `/experiences/${addToExperience}` : '/'
      navigate(targetUrl)
      return
    }

    // edit
    toastSuccess(t('uploadAgamiInterface.editSuccess'))
    if (refetchAgami) {
      refetchAgami()
    }
    window.scrollTo(0, 0)
  }, [setUploading, toastSuccess, addToExperience, agami, t, navigate, refetchAgami])

  const confirmUpload = (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()

    // @ts-expect-error too comples
    const is_draft = e?.nativeEvent?.submitter?.name === 'draft'

    if (!existingSpotEdit && !mediaFile) {
      toastError(t('uploadAgamiInterface.needMediaFile'))
      return
    }

    if (isEmpty(formData)) {
      return
    }

    setUploading(true)

    const mediaFilename = cleanFileName({ file: mediaFile })

    upsertAgamiSpot({
      ...formData,
      userDefaultExpId: user!.defaultExperienceId,
      spotId: agami?.id,
      audioContentPath: agami?.audio_content_path,
      spotName: formData.spotName.trim(),
      userId: user!.id,
      mediaFile,
      mediaFilename,
      is_draft,
      onSuccess,
      onError: (error) => {
        logger.error(error, 'UploadAgami')
        toastError(t('uploadAgamiInterface.uploadError', { error: error.message }))
        setUploading(false)
      },
    })
  }

  const submitButtonDisabled = uploading || (formData.type === 2 && !formData.manual_code)

  const confirmButtonLabel = React.useMemo(
    () =>
      isPublic ? t('uploadAgamiInterface.confirmAndMakePublic') : t('uploadAgamiInterface.confirmAndSave'),
    [isPublic, t]
  )

  const canDraftAgami = useFeatureAuth(FEATURES.DRAFT_AGAMI)
  const experimentalFeats = useFeatureAuth(FEATURES.EXPERIMENTAL_FEATURES)

  const draftButtonLabel = React.useMemo(() => {
    if (!agami) return t('uploadAgamiInterface.saveAsDraft')
    if (!agami?.is_draft) return t('uploadAgamiInterface.convertToDraft')
    return t('uploadAgamiInterface.saveEditsAndKeepAsDraft')
  }, [agami, t])

  return (
    <div className="w-full lg:w-11/12 my-10">
      {agami && (
        <div className="flex gap-2 mb-4">
          <Typography variant="small">
            {t('uploadAgamiInterface.created')}: <strong>{formatDateTimeString(agami?.inserted_at)}</strong>
          </Typography>
          <Typography variant="small">
            {' | '}
            {t('uploadAgamiInterface.lastUpdate')}: <strong>{formatDateTimeString(agami?.updated_at)}</strong>
          </Typography>
        </div>
      )}

      {/* A proposal gets ITS banner, not the generic draft one (05.5): whoever
          opens this form is deciding on someone else's word, and the banner —
          with the title AS PROPOSED — is the one place that says so. Without a
          pending submission the form stays identical to what it always was. */}
      {agami && hasPendingSubmission(agami) ? (
        <ProposalBanner agami={agami} />
      ) : (
        agami?.is_draft && (
          <Alert
            color="blue-gray"
            variant={darkMode ? 'filled' : 'ghost'}
            className="mb-4"
            icon={<PiCircleDashed className="h-6 w-6" />}
          >
            <Trans
              i18nKey="uploadAgamiInterface.draftMessage"
              components={{
                em: <em />,
                strong: <strong />,
              }}
            />
          </Alert>
        )
      )}

      <form className="flex flex-col gap-4" onSubmit={confirmUpload}>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 xl:mb-4">
          <AgamiMediaFileSelector
            agami={agami}
            mediaFile={mediaFile}
            setMediaFile={setMediaFile}
            formData={formData}
            handleDataChange={handleDataChange}
          />

          <div className="w-full h-full flex flex-col bg-white dark:bg-primary rounded-lg p-4 shadow-sm gap-4">
            <Typography className="font-bold text-primary text-xl font-barlow">
              {t('uploadAgamiInterface.details')}
            </Typography>
            <div className="flex flex-col gap-2 h-full">
              <Input
                className="w-full"
                value={formData.spotName}
                onChange={(e) => handleDataChange('spotName', e.currentTarget.value)}
                type="text"
                label={t('uploadAgamiInterface.nameLabel')}
                required
                disabled={uploading}
                data-testid="agami-name-input"
                {...(darkMode && { color: 'white' })}
              />
              <Textarea
                className="w-full text-primary-600 focus:text-primary-700 dark:!text-base-gray-500 dark:focus:text-base-gray-400"
                rows={5}
                value={formData.description}
                onChange={(e) => handleDataChange('description', e.currentTarget.value)}
                label={t('uploadAgamiInterface.descriptionLabel')}
                disabled={uploading}
                data-testid="agami-description-input"
                {...(darkMode && { color: 'blue-gray' })}
                labelProps={{
                  className:
                    'text-gray-600 dark:!text-gray-200 peer-focus:!text-gray-800 dark:peer-focus:!text-gray-100',
                }}
              />
            </div>
          </div>
        </div>

        {userExperiences && userExperiences?.length > 0 && (
          <AgamiExperienceSelector
            userExperiences={userExperiences}
            formData={formData}
            setFormData={setFormData}
          />
        )}

        {showMakePublicOption && (
          <AgamiVisibilitySelector
            isPublic={isPublic ?? false}
            setIsPublic={setIsPublic}
            formData={formData}
          />
        )}

        {manualSpotsAuth && (
          <AgamiTypeSelector
            isPublic={isPublic ?? false}
            formData={formData}
            handleDataChange={handleDataChange}
            availableSpotTypes={availableSpotTypes}
          />
        )}

        {/* GEO TYPE */}
        {formData.type === 1 && (
          <div className="flex flex-col gap-4 bg-white dark:bg-primary rounded-lg p-4 shadow-sm">
            <SpotPositionSelector
              formData={formData}
              setFormData={setFormData}
              handleDataChange={handleDataChange}
              checkInputIsValid={checkInputIsValid}
              uploading={uploading}
            />
            <SpotPositionPreview
              interactive
              latitude={formData.latitude}
              setLatitude={(lat) => handleDataChange('latitude', lat)}
              longitude={formData.longitude}
              setLongitude={(lon) => handleDataChange('longitude', lon)}
              radius={formData.radius ?? DEFAULT_SPOT_RADIUS}
              deadband={formData.deadband ?? DEFAULT_SPOT_DEADBAND}
            />
          </div>
        )}

        {/* MANUAL TYPE */}
        {formData.type === 2 && (
          <div className="flex flex-col gap-4 bg-white dark:bg-primary rounded-lg p-4 shadow-sm">
            <SpotManualCodeSelector
              formData={formData}
              uploading={uploading}
              handleDataChange={handleDataChange}
              spotId={agami?.id as string}
            />
            <LandingPageSelector
              value={formData.landing_page_code_id}
              onChange={(id) => handleDataChange('landing_page_code_id', id)}
              disabled={uploading}
            />
          </div>
        )}

        {experimentalFeats && agami && <AgamiPreGeneratedSignedUrl agami={agami} />}

        {/* ⚠️ NO "Approve" button, on purpose (05.5, D15): for a proposed word
            the publish button below IS the approval — publishing flips is_draft
            and the database trigger records the outcome. A second button would
            mean two ways of making the same word public, and the form must
            never write on experience_draft_submissions itself. */}
        <div className="flex flex-col md:flex-row gap-4 mt-8">
          <Button
            type="submit"
            className="w-full md:w-auto"
            disabled={submitButtonDisabled}
            data-testid="agami-upload-confirm-btn"
          >
            {uploading ? (
              <div className="flex items-center gap-2">
                <Spinner className="h-4 w-4 mx-auto" />
              </div>
            ) : (
              confirmButtonLabel
            )}
          </Button>

          {canDraftAgami && (
            <Button
              color={darkMode ? 'white' : ('primary' as colors)}
              type="submit"
              name="draft"
              variant="outlined"
              className="w-full md:w-auto"
              disabled={uploading}
              data-testid="draft-upload-button"
            >
              {draftButtonLabel}
            </Button>
          )}

          {existingSpotEdit && (
            <Button
              type="button"
              variant="text"
              className="w-full md:w-auto"
              color="red"
              onClick={() => navigate(-1)}
              disabled={uploading}
              data-testid="cancel-edit-button"
            >
              {t('common.cancel')}
            </Button>
          )}
        </div>
      </form>
    </div>
  )
}

export default UploadAgamiInterface
